<template>
  <div class="container py-5">

    <div class="buttons">
      <button 
        class="button is-primary"
        v-if="!editing"
        @click="editing = true">
        Edit
      </button>
      <button
        class="button is-link"
        v-if="editing"
        @click="saveChanges()">
        Save Changes
      </button>
      <button
        class="button"
        v-if="editing"
        @click="$router.go(-1)">
        Go back
      </button>
    </div>
    
    <div class="columns is-multiline" v-if="company">
      <div class="column is-4">
        <label class="label">Company Name</label>
        <v-text-field 
          :disabled="!editing"
          v-model="company.name"/>
      </div>

      <div class="column is-3">
        <label class="label">Phone Number</label>
        <v-text-field
          :disabled="!editing"
          v-model="company.phone1"/>
      </div>

      <div class="column is-3">
        <div class="label-and-button pb-2">
          <label class="label mb-0">Company Category*</label>
          <span 
            class="tag pointer" 
            @click="managingCategories = true">
            <span class="icon is-small">
              <i class="fas fa-plus"></i>
            </span>
            <span>Manage Categories</span>
          </span>
        </div>
        <v-select
          :disabled="!editing"
          :options="categories"
          item-text="category"
          item-value="id"
          v-model="company.category_id"
        />
      </div>

      <div class="column is-2">
        <label class="label">Average Rating</label>
        <v-star-rating
          v-if="company.avg_rating"
          class="pt-2 has-text-left"
          v-model="company.avg_rating"/>
        <div v-else class="has-text-grey-light pt-2">
          No rating available
        </div>
      </div>

      <div class="column is-4">
        <label class="label">Address</label>
        <v-text-field
          :disabled="!editing"
          v-model="company.address"/>
      </div>

      <div class="column is-2">
        <label class="label">City</label>
        <v-text-field
          :disabled="!editing"
          v-model="company.city"/>
      </div>

      <div class="column is-2">
        <label class="label">Province</label>
        <v-text-field
          :disabled="!editing"
          v-model="company.province"/>
      </div>

      <div class="column is-2">
        <label class="label">Postal Code</label>
        <v-text-field
          :disabled="!editing"
          v-model="company.postalcode"/>
      </div>

      <div class="column is-2">
        <label class="label">Country</label>
        <v-text-field
          :disabled="!editing"
          v-model="company.country"/>
      </div>

    </div>

    <div v-if="company" class="mt-5">

      <div class="level mb-3">
        <div class="title is-5 mb-0">
          Company Contacts
        </div>
        <button 
          @click="addingContact = true"
          class="button is-primary is-small">
          <span class="icon">
            <i class="fas fa-plus"></i>
          </span>
          <span>Add a contact</span>
        </button>
      </div>

      <article 
        v-if="company.contacts.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no contacts for this company.
        </div>
      </article>

      <table 
        v-else
        class="table is-hoverable is-clickable is-fullwidth">
        <thead>
          <tr>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="contact in company.contacts"
            :key="contact.id">
            <td 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex"> 
              {{ contact[header.field] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <add-contact-modal 
      v-if="company"
      v-model="addingContact"
      :company-id="company.id"
      @success="addContactSuccess"/>

    <manage-categories-modal 
      v-model="managingCategories"/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VStarRating from '@/components/base/VStarRating'
import AddContactModal from '@/components/ticketing/AddContactModal'
import ManageCategoriesModal from '@/components/ticketing/ManageCategoriesModal'

import { mapState, mapActions } from 'vuex'

export default {
  components: { 
    VTextField, VSelect, AddContactModal, VStarRating, ManageCategoriesModal 
  },
  data() {
    return {
      company: null,
      editing: false,
      addingContact: false,
      managingCategories: false,
      headers: [
        { label: 'First Name', field: 'first_name' },
        { label: 'Last Name', field: 'last_name' },
        { label: 'Position', field: 'position' },
        { label: 'Email', field: 'email' },
        { label: 'Phone', field: 'phone1'}
      ]
    }
  },
  computed: {
    ...mapState("tickets", ["categories"]),
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("tickets", ["updateCompany"]),
    initialize() {
      if (this.$route.params.company) {
        this.company = this.$route.params.company;
      } else {
        this.$http.ticketingCompany.get(this.$route.params.companyId).then(
          resp => {
            this.company = resp.data;
          }
        )
      }
    },
    saveChanges() {
      let companyCopy = Object.assign({}, this.company);
      delete companyCopy.contacts;
      delete companyCopy.category;

      this.updateCompany({ 
        companyId: companyCopy.id, 
        data: companyCopy 
      }).then(() => {
        this.editing = false;
      })
    },
    addContactSuccess(contact) {
      this.company.contacts.push(contact);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.label-and-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .tag {
    cursor: pointer;
    &:hover {
      background: gainsboro;
    }
  }
}
</style>